import React from 'react';

function LinkButton({ action, secondary = false }) {
  if (!action?.label || !action?.href) return null;
  return <a className={secondary ? 'sb-button sb-button-secondary' : 'sb-button'} href={action.href}>{action.label}</a>;
}

function resolveManagedAsset(assetId, fallbackUrl, assetResolver) {
  if (assetId && typeof assetResolver === 'function') return assetResolver(assetId) || fallbackUrl || '';
  return fallbackUrl || '';
}


function resolveImageSizing(props, prefix = 'image') {
  const mode = props?.[`${prefix}SizeMode`] || 'auto';
  const raw = Number(props?.[`${prefix}SizeValue`]);
  if (!Number.isFinite(raw) || raw <= 0 || mode === 'auto') return undefined;

  if (mode === 'width') {
    const width = Math.max(10, Math.min(100, raw));
    return { width: `${width}%`, maxWidth: '100%', height: 'auto', aspectRatio: 'auto', objectFit: 'contain' };
  }

  if (mode === 'height') {
    const height = Math.max(24, Math.min(720, raw));
    return { height: `${height}px`, width: 'auto', maxWidth: '100%', aspectRatio: 'auto', objectFit: 'contain' };
  }

  return undefined;
}


function resolveLogoOffset(props) {
  const top = Number(props?.logoPaddingTop ?? 0);
  const left = Number(props?.logoPaddingLeft ?? 0);
  const safeTop = Number.isFinite(top) ? Math.max(0, Math.min(96, top)) : 0;
  const safeLeft = Number.isFinite(left) ? Math.max(0, Math.min(96, left)) : 0;
  if (!safeTop && !safeLeft) return undefined;

  // These controls are named as padding in the editor because that is how users think
  // about adding breathing room around tight logo artwork. Use transform instead of
  // CSS padding so the logo can move visually without increasing the header/footer
  // row height or pushing sibling elements such as the CTA button down.
  return {
    transform: `translate(${safeLeft}px, ${safeTop}px)`,
    transformOrigin: 'top left'
  };
}

function resolveLogoStyle(props) {
  return { ...(resolveImageSizing(props, 'logo') || {}), ...(resolveLogoOffset(props) || {}) };
}

function resolveVisualBoxClass(style, defaultClass = '') {
  const s = style ?? {};
  const bg = ['default', 'surface', 'surfaceAlt', 'accentTint', 'transparent'].includes(s.background) ? s.background : 'default';
  const border = ['default', 'none', 'subtle', 'accent'].includes(s.border) ? s.border : 'default';
  const radius = ['default', 'none', 'small', 'medium', 'large'].includes(s.radius) ? s.radius : 'default';
  const shadow = ['default', 'none', 'subtle', 'medium', 'strong'].includes(s.shadow) ? s.shadow : 'default';
  return `${defaultClass} sb-visual-box sb-v-bg-${bg} sb-v-border-${border} sb-v-radius-${radius} sb-v-shadow-${shadow}`.trim();
}

function resolveVisualBoxStyle(style) {
  const s = style ?? {};
  const map = {
    default: undefined,
    surface: 'var(--sb-surface)',
    surfaceAlt: 'var(--sb-surface-alt)',
    accentTint: 'color-mix(in srgb, var(--sb-accent) 10%, var(--sb-surface))',
    transparent: 'transparent'
  };
  return s.background && s.background !== 'default' ? { '--sb-visual-bg': map[s.background] } : undefined;
}

function sectionClass(block, baseClass = 'sb-section') {
  const defaultTone = block?.type === 'content.image-split@1' ? 'alt' : 'default';
  const tone = block?.props?.sectionTone || defaultTone;
  const toneClass = tone === 'default' ? '' : ` sb-section-tone-${tone}`;
  return `${baseClass}${toneClass}`;
}

function blockAttrs(block) {
  return block?.id ? { id: block.id, 'data-sb-block-id': block.id } : {};
}

function blockNavLabel(block) {
  const p = block?.props ?? {};
  if (p.navLabel) return p.navLabel;
  if (p.heading) return p.heading;
  if (p.eyebrow) return p.eyebrow;
  if (block?.type?.startsWith('hero')) return 'Home';
  if (block?.type?.startsWith('features')) return 'Items';
  if (block?.type?.startsWith('content')) return 'Details';
  if (block?.type?.startsWith('text')) return 'About';
  if (block?.type?.startsWith('cta')) return 'Next step';
  if (block?.type?.startsWith('form')) return 'Contact';
  return '';
}

function getHeaderNavigationItems(page, props, snapshot) {
  if (Array.isArray(props?.items) && props.items.length) return props.items;
  const pages = Array.isArray(snapshot?.pages) ? snapshot.pages : [];
  const pageItems = pages
    .filter((candidate) => candidate?.showInHeaderNav === true && candidate?.slug)
    .map((candidate) => ({ label: candidate.navLabel || candidate.title || candidate.slug, href: candidate.slug || '/' }));
  const blocks = Array.isArray(page?.blocks) ? page.blocks : [];
  const sectionItems = blocks
    .filter((block) => block?.id && block?.props?.showInHeaderNav === true && !block?.type?.startsWith('header') && !block?.type?.startsWith('footer'))
    .map((block) => ({ label: blockNavLabel(block), href: `#${block.id}` }))
    .filter((item) => item.label && item.href);

  const seen = new Set();
  return [...pageItems, ...sectionItems].filter((item) => {
    const key = `${item.label}|${item.href}`;
    if (!item.label || !item.href || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function HeaderBlock({ block, page, snapshot, assetResolver }) {
  const props = block.props ?? {};
  const items = getHeaderNavigationItems(page, props, snapshot);
  const logoSrc = resolveManagedAsset(props.logoAssetId, props.logoUrl, assetResolver);
  return <header className="sb-header" {...blockAttrs(block)}><div className="sb-shell sb-header-inner">
    <a className="sb-brand" href="/">{logoSrc ? <img src={logoSrc} alt={props.logoAlt || snapshot.site?.name || 'Home'} style={resolveLogoStyle(props)} /> : <span>{props.brandName || snapshot.site?.name}</span>}</a>
    <nav aria-label="Primary navigation">{items.map((item) => <a key={`${item.label}-${item.href}`} href={item.href}>{item.label}</a>)}</nav>
    <LinkButton action={props.action} />
  </div></header>;
}

export function HeroSplitBlock({ block, assetResolver, quickNotice }) {
  const p = block.props ?? {};
  const slides = normalizeHeroSlides(p, quickNotice);
  const useCarousel = (p.heroMode || 'static') === 'carousel' && slides.length > 0;
  if (useCarousel) return <HeroCarouselBlock block={block} slides={slides} assetResolver={assetResolver} />;
  return <section className={sectionClass(block, 'sb-section sb-hero')} {...blockAttrs(block)}><div className="sb-shell sb-hero-grid">
    <HeroCopyContent slide={p} />
    <div className="sb-hero-side-stack">
      {quickNotice?.displayMode === 'hero-card' ? <QuickNoticeHeroCard notice={quickNotice} /> : null}
      <HeroVisual slide={p} assetResolver={assetResolver} />
    </div>
  </div></section>;
}

function normalizeHeroSlides(props, quickNotice) {
  const configured = Array.isArray(props?.slides) ? props.slides.filter(Boolean) : [];
  const slides = configured.length ? configured : [{
    id: 'static-hero-slide',
    eyebrow: props?.eyebrow || '',
    heading: props?.heading || '',
    body: props?.body || '',
    imageAssetId: props?.imageAssetId || '',
    imageUrl: props?.imageUrl || '',
    imageAlt: props?.imageAlt || '',
    imageSizeMode: props?.imageSizeMode || 'auto',
    imageSizeValue: props?.imageSizeValue || '',
    primaryAction: props?.primaryAction || {},
    secondaryAction: props?.secondaryAction || {}
  }];
  if (quickNotice?.displayMode === 'carousel-slide') {
    return [quickNoticeToSlide(quickNotice), ...slides];
  }
  return slides;
}

function quickNoticeToSlide(notice) {
  return {
    id: `quick-notice-${notice.tone || 'info'}`,
    eyebrow: notice.tone === 'urgent' ? 'Important notice' : 'Notice',
    heading: notice.title || 'Notice',
    body: notice.body || '',
    imageAssetId: notice.imageAssetId || '',
    imageUrl: notice.imageUrl || '',
    imageAlt: notice.imageAlt || '',
    imageSizeMode: notice.imageSizeMode || 'auto',
    imageSizeValue: notice.imageSizeValue || '',
    primaryAction: notice.action || {},
    secondaryAction: {}
  };
}

function QuickNoticeHeroCard({ notice }) {
  if (!notice) return null;
  return <aside className={`sb-quick-notice sb-quick-notice-hero-card sb-quick-notice-${notice.tone}`} aria-label={notice.tone === 'urgent' ? 'Urgent notice' : 'Notice'}>
    <p className="sb-eyebrow">{notice.tone === 'urgent' ? 'Important notice' : 'Notice'}</p>
    <h2>{notice.title || 'Notice'}</h2>
    {notice.body ? <p>{notice.body}</p> : null}
    {notice.action ? <LinkButton action={notice.action} /> : null}
  </aside>;
}

function HeroCarouselBlock({ block, slides, assetResolver }) {
  const p = block.props ?? {};
  const [activeIndex, setActiveIndex] = React.useState(0);
  const count = slides.length;
  const seconds = Math.max(3, Math.min(12, Number(p.carouselIntervalSeconds) || 6));
  const autoplay = p.carouselAutoplay !== false && count > 1;
  const containerStyle = ['open', 'soft', 'framed', 'contained'].includes(p.carouselContainerStyle) ? p.carouselContainerStyle : 'contained';
  const navStyle = ['dots', 'dots-arrows', 'pills'].includes(p.carouselNavStyle) ? p.carouselNavStyle : 'dots';
  const carouselHeight = ['auto', 'compact', 'medium', 'tall'].includes(p.carouselHeight) ? p.carouselHeight : 'medium';

  React.useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(0, count - 1)));
  }, [count]);

  React.useEffect(() => {
    if (!autoplay) return undefined;
    const timer = window.setInterval(() => setActiveIndex((current) => (current + 1) % count), seconds * 1000);
    return () => window.clearInterval(timer);
  }, [autoplay, count, seconds]);

  function go(delta) { setActiveIndex((current) => (current + delta + count) % count); }
  const activeSlide = slides[activeIndex] || slides[0];
  const slideFit = getHeroSlideFit(activeSlide, carouselHeight);

  const showArrows = navStyle === 'dots-arrows' || navStyle === 'pills';

  return <section className={`${sectionClass(block, 'sb-section sb-hero')} sb-hero-carousel sb-hero-carousel-${containerStyle} sb-hero-nav-${navStyle} sb-hero-height-${carouselHeight} sb-hero-density-${slideFit.density} sb-hero-fit-${slideFit.fit} ${slideFit.hasImage ? 'sb-hero-has-media' : 'sb-hero-no-media'}`} {...blockAttrs(block)}>
    <div className="sb-shell">
      <div className={resolveVisualBoxClass(p.carouselBoxStyle, "sb-hero-carousel-stage")} style={resolveVisualBoxStyle(p.carouselBoxStyle)}>
        <div className="sb-hero-grid sb-hero-slide-grid" key={activeSlide?.id || activeIndex}>
          <HeroCopyContent slide={activeSlide} />
          <HeroVisual slide={activeSlide} assetResolver={assetResolver} />
        </div>
        {count > 1 ? <div className="sb-hero-carousel-controls" aria-label="Hero carousel controls">
          {showArrows ? <button type="button" className="sb-hero-arrow" onClick={() => go(-1)} aria-label="Show previous hero item">‹</button> : null}
          <div className="sb-hero-dots" role="tablist" aria-label="Hero carousel slides">
            {slides.map((slide, index) => <button key={slide.id || index} type="button" className={index === activeIndex ? 'active' : ''} onClick={() => setActiveIndex(index)} aria-label={`Show hero item ${index + 1}: ${slide.heading || 'Untitled'}`} aria-selected={index === activeIndex} />)}
          </div>
          {showArrows ? <button type="button" className="sb-hero-arrow" onClick={() => go(1)} aria-label="Show next hero item">›</button> : null}
        </div> : null}
      </div>
    </div>
  </section>;
}


function getHeroSlideFit(slide, height = 'medium') {
  if (height === 'auto') return { density: 'normal', fit: 'comfortable', hasImage: !!(slide?.imageAssetId || slide?.imageUrl) };

  const heading = String(slide?.heading || '').trim();
  const body = String(slide?.body || '').trim();
  const paragraphs = body.split(/\n{2,}|\r?\n/).map((x) => x.trim()).filter(Boolean);
  const buttons = (slide?.primaryAction?.label && slide?.primaryAction?.href ? 1 : 0) + (slide?.secondaryAction?.label && slide?.secondaryAction?.href ? 1 : 0);
  const hasImage = !!(slide?.imageAssetId || slide?.imageUrl);
  const wordCount = `${heading} ${body}`.trim().split(/\s+/).filter(Boolean).length;
  const longestWord = `${heading} ${body}`.split(/\s+/).reduce((max, word) => Math.max(max, word.length), 0);
  const headingLinePressure = Math.max(0, heading.length - 34) * 3.5 + Math.max(0, heading.split(/\s+/).filter(Boolean).length - 7) * 18;
  const bodyPressure = body.length * 1.05 + paragraphs.length * 42;
  const mediaPressure = hasImage ? 135 : 30;
  const controlPressure = buttons * 46 + Math.max(0, longestWord - 18) * 10;
  const score = heading.length * 1.6 + headingLinePressure + bodyPressure + mediaPressure + controlPressure + wordCount * 4;
  const limits = {
    compact: { dense: 340, veryDense: 500, critical: 660 },
    medium: { dense: 470, veryDense: 670, critical: 860 },
    tall: { dense: 590, veryDense: 820, critical: 1040 }
  }[height] || { dense: 470, veryDense: 670, critical: 860 };

  if (score >= limits.critical) return { density: 'very-dense', fit: 'critical', hasImage };
  if (score >= limits.veryDense) return { density: 'very-dense', fit: 'tight', hasImage };
  if (score >= limits.dense) return { density: 'dense', fit: 'dense', hasImage };
  return { density: 'normal', fit: 'comfortable', hasImage };
}

function HeroCopyContent({ slide }) {
  const paragraphs = String(slide?.body || '').split(/\n{2,}|\r?\n/).map((x) => x.trim()).filter(Boolean);
  return <div className="sb-hero-copy">
    {slide?.eyebrow ? <p className="sb-eyebrow">{slide.eyebrow}</p> : null}
    {slide?.heading ? <h1>{slide.heading}</h1> : null}
    {paragraphs.length ? <div className="sb-lead-stack">{paragraphs.map((text, index) => <p className="sb-lead" key={index}>{text}</p>)}</div> : null}
    <div className="sb-actions"><LinkButton action={slide?.primaryAction} /><LinkButton action={slide?.secondaryAction} secondary /></div>
  </div>;
}

function HeroVisual({ slide, assetResolver }) {
  const imageSrc = resolveManagedAsset(slide?.imageAssetId, slide?.imageUrl, assetResolver);
  return imageSrc ? <div className="sb-hero-media"><img src={imageSrc} alt={slide?.imageAlt || ''} style={resolveImageSizing(slide, 'image')} /></div> : <div className="sb-hero-accent" aria-hidden="true" />;
}

export function RichTextBlock({ block }) {
  const p = block.props ?? {};
  return <section className={sectionClass(block)} {...blockAttrs(block)}><div className="sb-shell sb-reading"><p className="sb-eyebrow">{p.eyebrow}</p><h2>{p.heading}</h2>{(p.paragraphs ?? []).map((text, index) => <p key={index}>{text}</p>)}</div></section>;
}

export function ImageSplitBlock({ block, assetResolver }) {
  const p = block.props ?? {};
  const imageSrc = resolveManagedAsset(p.imageAssetId, p.imageUrl, assetResolver);
  return <section className={sectionClass(block)} {...blockAttrs(block)}><div className={`sb-shell sb-split ${p.imagePosition === 'right' ? 'sb-split-reverse' : ''}`}> 
    <div className={resolveVisualBoxClass(p.imageBoxStyle, "sb-split-media")} style={resolveVisualBoxStyle(p.imageBoxStyle)}>{imageSrc ? <img src={imageSrc} alt={p.imageAlt || ''} style={resolveImageSizing(p, 'image')} /> : null}</div>
    <div><p className="sb-eyebrow">{p.eyebrow}</p><h2>{p.heading}</h2><p>{p.body}</p><LinkButton action={p.action} /></div>
  </div></section>;
}

export function FeatureGridBlock({ block, assetResolver, formSubmitBaseUrl, currentHost }) {
  const p = block.props ?? {};
  const items = Array.isArray(p.items) ? p.items : [];
  const cardStyle = p.cardStyle || 'simple';
  const [activeItem, setActiveItem] = React.useState(null);
  const [leadItem, setLeadItem] = React.useState(null);
  const activeDetail = activeItem && getItemDetail(activeItem, assetResolver);

  function documentUrl(item) {
    return item?.documentAssetId ? resolveManagedAsset(item.documentAssetId, '', assetResolver) : '';
  }

  return <section className={sectionClass(block)} {...blockAttrs(block)}>
    <div className="sb-shell">
      <div className="sb-section-heading"><p className="sb-eyebrow">{p.eyebrow}</p><h2>{p.heading}</h2><p>{p.body}</p></div>
      <div className={`sb-grid sb-card-grid sb-card-grid-${cardStyle}`}>
        {items.map((item, index) => {
          const isDocument = (item.itemType || '') === 'document';
          const detail = getItemDetail(item, assetResolver);
          const cardImage = resolveManagedAsset(item.imageAssetId, item.imageUrl, assetResolver);
          const hasModal = (item.detailMode || 'none') === 'modal' && !!detail;
          const linkLabel = item.linkLabel || (isDocument ? 'Download' : hasModal ? 'Learn more' : 'View details');
          const icon = item.icon || (isDocument ? documentIcon(item.documentMimeType || item.documentFileName) : defaultItemIcon(cardStyle, index));
          const isTestimonial = cardStyle === 'testimonial';
          const isBadge = cardStyle === 'badge';
          const docUrl = documentUrl(item);
          const article = <article data-sb-item-id={item.id || index} className={`${resolveVisualBoxClass(p.cardBoxStyle, "sb-card sb-item-card")} ${isDocument ? 'sb-document-card' : ''}`} style={resolveVisualBoxStyle(p.cardBoxStyle)}>
            {cardImage && !isDocument && (cardStyle === 'image' || cardStyle === 'horizontal') ? <div className="sb-item-card-media"><img src={cardImage} alt={item.imageAlt || ''} /></div> : null}
            <div className="sb-item-card-body">
              {(cardStyle === 'icon' || cardStyle === 'accent' || isDocument) ? <span className={isDocument ? 'sb-document-icon' : 'sb-card-icon'} aria-hidden="true">{icon}</span> : null}
              {item.eyebrow ? <p className="sb-card-kicker">{item.eyebrow}</p> : null}
              {isTestimonial && !isDocument ? <span className="sb-quote-mark" aria-hidden="true">“</span> : null}
              {isBadge && !isDocument ? <span className="sb-badge-mark" aria-hidden="true">★</span> : null}
              <h3>{item.heading}</h3>
              <p>{item.body}</p>
              {isDocument ? <p className="sb-document-meta">{documentMeta(item)}</p> : null}
              {item.attribution && !isDocument ? <p className="sb-card-attribution">{item.attribution}</p> : null}
              {isDocument ? (item.guardWithLeadForm === true ? <button type="button" className="sb-text-button" disabled={!item.documentAssetId} onClick={() => setLeadItem(item)}>{linkLabel}</button> : docUrl ? <a href={docUrl} download>{linkLabel}</a> : <span className="sb-form-note">Document not selected</span>) : hasModal ? <button type="button" className="sb-text-button" onClick={() => setActiveItem(item)}>{linkLabel}</button> : item.href ? <a href={item.href}>{item.linkLabel || 'Learn more'}</a> : null}
            </div>
          </article>;
          return <React.Fragment key={item.id || index}>{article}</React.Fragment>;
        })}
      </div>
    </div>
    {activeDetail ? <ItemDetailModal item={activeItem} detail={activeDetail} assetResolver={assetResolver} onClose={() => setActiveItem(null)} /> : null}
    {leadItem ? <DocumentLeadModal item={leadItem} assetResolver={assetResolver} formSubmitBaseUrl={formSubmitBaseUrl} currentHost={currentHost} onClose={() => setLeadItem(null)} /> : null}
  </section>;
}

function defaultItemIcon(style, index) {
  if (style === 'badge') return '★';
  const icons = ['✓', '↗', '◆', '○', '✦', '→'];
  return icons[index % icons.length];
}



function documentIcon(value) {
  const v = String(value || '').toLowerCase();
  if (v.includes('pdf')) return 'PDF';
  if (v.includes('word') || v.endsWith('.doc') || v.endsWith('.docx')) return 'DOC';
  if (v.includes('excel') || v.endsWith('.xls') || v.endsWith('.xlsx') || v.endsWith('.csv')) return 'XLS';
  if (v.includes('powerpoint') || v.endsWith('.ppt') || v.endsWith('.pptx')) return 'PPT';
  return 'FILE';
}

function documentMeta(item) {
  const bits = [];
  const type = documentIcon(item.documentMimeType || item.documentFileName);
  if (type) bits.push(type);
  if (Number(item.documentSizeBytes) > 0) bits.push(formatFileSize(Number(item.documentSizeBytes)));
  return bits.join(' · ') || 'Downloadable document';
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function DocumentLeadModal({ item, assetResolver, formSubmitBaseUrl, currentHost, onClose }) {
  const [values, setValues] = React.useState({ name: '', email: '' });
  const [status, setStatus] = React.useState('idle');
  const [message, setMessage] = React.useState('');
  const startedAt = React.useRef(new Date().toISOString());
  const directUrl = resolveManagedAsset(item.documentAssetId, '', assetResolver);
  const canSubmit = !!formSubmitBaseUrl && !!item.documentAssetId;

  React.useEffect(() => {
    function onKey(event) { if (event.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    setMessage('');
    const honeypot = event.currentTarget.elements?.sb_company_website?.value || '';
    try {
      const response = await fetch(`${formSubmitBaseUrl.replace(/\/$/, '')}/public/websites/document-leads/${encodeURIComponent(item.documentAssetId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, documentTitle: item.heading, host: currentHost || window.location.hostname, honeypot, startedAtUtc: startedAt.current })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || 'Please enter your name and a valid email address.');
      setStatus('success');
      setMessage('Thanks — your download is ready.');
      const url = payload.downloadUrl || directUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'The document could not be prepared.');
    }
  }

  return <div className="sb-detail-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="sb-detail-modal sb-document-lead-modal" role="dialog" aria-modal="true" aria-labelledby={`document-${item.id || 'item'}-title`}>
      <button type="button" className="sb-detail-close" onClick={onClose} aria-label="Close download form">×</button>
      <div className="sb-detail-content">
        <p className="sb-eyebrow">Document download</p>
        <h2 id={`document-${item.id || 'item'}-title`}>{item.heading || 'Download document'}</h2>
        {item.body ? <p>{item.body}</p> : null}
        <p className="sb-document-meta">{documentMeta(item)}</p>
        <form className="sb-form sb-document-lead-form" onSubmit={submit}>
          <label className="sb-hidden-field" aria-hidden="true">Company website<input name="sb_company_website" tabIndex="-1" autoComplete="off" /></label>
          <label className="sb-form-field"><span>Name *</span><input required value={values.name} onChange={(e) => setValues((old) => ({ ...old, name: e.target.value }))} /></label>
          <label className="sb-form-field"><span>Email *</span><input type="email" required value={values.email} onChange={(e) => setValues((old) => ({ ...old, email: e.target.value }))} /></label>
          {message ? <p className={status === 'error' ? 'sb-form-message sb-form-error' : 'sb-form-message'}>{message}</p> : null}
          <button className="sb-button" type="submit" disabled={!canSubmit || status === 'submitting'}>{status === 'submitting' ? 'Preparing…' : item.linkLabel || 'Download'}</button>
          {!canSubmit ? <p className="sb-form-note">Document lead capture is available on the published site.</p> : null}
          {status === 'success' && directUrl ? <a className="sb-text-button" href={directUrl} target="_blank" rel="noreferrer">Open download again</a> : null}
        </form>
      </div>
    </div>
  </div>;
}

function getItemDetail(item, assetResolver) {
  if (!item) return null;
  const title = item.detailTitle || item.heading;
  const body = item.detailBody || '';
  const imageSrc = resolveManagedAsset(item.detailImageAssetId, item.detailImageUrl, assetResolver) || resolveManagedAsset(item.imageAssetId, item.imageUrl, assetResolver);
  const action = item.detailAction?.label && item.detailAction?.href ? item.detailAction : null;
  if (!body && !imageSrc && !action) return null;
  return { title, body, imageSrc, action };
}

function ItemDetailModal({ item, detail, onClose }) {
  React.useEffect(() => {
    function onKey(event) { if (event.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const paragraphs = String(detail.body || '').split(/\n{2,}/).map((x) => x.trim()).filter(Boolean);
  return <div className="sb-detail-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="sb-detail-modal" role="dialog" aria-modal="true" aria-labelledby={`detail-${item.id || 'item'}-title`}>
      <button type="button" className="sb-detail-close" onClick={onClose} aria-label="Close detail">×</button>
      {detail.imageSrc ? <div className="sb-detail-media"><img src={detail.imageSrc} alt={item.detailImageAlt || item.imageAlt || ''} /></div> : null}
      <div className="sb-detail-content">
        {item.eyebrow ? <p className="sb-eyebrow">{item.eyebrow}</p> : null}
        <h2 id={`detail-${item.id || 'item'}-title`}>{detail.title}</h2>
        {paragraphs.length ? paragraphs.map((text, index) => <p key={index}>{text}</p>) : null}
        {detail.action ? <LinkButton action={detail.action} /> : null}
      </div>
    </div>
  </div>;
}


export function CtaBandBlock({ block }) {
  const p = block.props ?? {};
  return <section className={sectionClass(block)} {...blockAttrs(block)}><div className="sb-shell"><div className="sb-cta"><div><p className="sb-eyebrow">{p.eyebrow}</p><h2>{p.heading}</h2><p>{p.body}</p></div><div className="sb-actions"><LinkButton action={p.primaryAction} /><LinkButton action={p.secondaryAction} secondary /></div></div></div></section>;
}


export function FormBlock({ block, formSubmitBaseUrl, currentHost }) {
  const p = block.props ?? {};
  const config = p.formConfig ?? {};
  const fields = Array.isArray(config.fields) ? config.fields : [];
  const [values, setValues] = React.useState({});
  const [status, setStatus] = React.useState('idle');
  const [message, setMessage] = React.useState('');
  const startedAt = React.useRef(new Date().toISOString());
  const canSubmit = !!p.formId && !!formSubmitBaseUrl;

  function update(key, value) { setValues((old) => ({ ...old, [key]: value })); }
  async function submit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    setMessage('');
    const honeypot = event.currentTarget.elements?.sb_company_website?.value || '';
    try {
      const response = await fetch(`${formSubmitBaseUrl.replace(/\/$/, '')}/public/websites/forms/${encodeURIComponent(p.formId)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ values, host: currentHost || window.location.hostname, honeypot, startedAtUtc: startedAt.current })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || payload.errors?.[0]?.message || 'Please check the form and try again.');
      setValues({});
      setStatus('success');
      setMessage(config.successMessage || 'Thanks — your message has been sent.');
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'The form could not be submitted.');
    }
  }

  return <section className={sectionClass(block)} {...blockAttrs(block)}>
    <div className="sb-shell sb-form-section">
      <div className="sb-section-heading"><p className="sb-eyebrow">{p.eyebrow}</p><h2>{p.heading}</h2><p>{p.body}</p></div>
      <form className="sb-form" onSubmit={submit}>
        <label className="sb-hidden-field" aria-hidden="true">Company website<input name="sb_company_website" tabIndex="-1" autoComplete="off" /></label>
        {fields.map((field) => <label key={field.id || field.key} className="sb-form-field"><span>{field.label}{field.required ? ' *' : ''}</span>{field.type === 'textarea' ? <textarea rows="5" placeholder={field.placeholder || ''} required={!!field.required} value={values[field.key] || ''} onChange={(e) => update(field.key, e.target.value)} /> : <input type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'} placeholder={field.placeholder || ''} required={!!field.required} value={values[field.key] || ''} onChange={(e) => update(field.key, e.target.value)} />}</label>)}
        {message && <p className={status === 'error' ? 'sb-form-message sb-form-error' : 'sb-form-message'}>{message}</p>}
        <button className="sb-button" type="submit" disabled={!canSubmit || status === 'submitting'}>{status === 'submitting' ? 'Sending…' : config.submitLabel || 'Send message'}</button>
        {!p.formId && <p className="sb-form-note">Choose a form in the editor before publishing this section.</p>}
      </form>
    </div>
  </section>;
}

export function FooterBlock({ block, snapshot, assetResolver }) {
  const p = block.props ?? {};
  const logoSrc = resolveManagedAsset(p.logoAssetId, p.logoUrl, assetResolver);
  return <footer className="sb-footer" {...blockAttrs(block)}><div className="sb-shell sb-footer-inner"><div>{logoSrc ? <img className="sb-footer-logo" src={logoSrc} alt={p.logoAlt || p.brandName || snapshot.site?.name || ''} style={resolveLogoStyle(p)} /> : <strong>{p.brandName || snapshot.site?.name}</strong>}<p>{p.body}</p></div><div>{(p.links ?? []).map((item) => <a key={`${item.label}-${item.href}`} href={item.href}>{item.label}</a>)}</div><small>{p.copyright || `© ${new Date().getFullYear()} ${p.brandName || snapshot.site?.name}`}</small></div></footer>;
}

export const coreBlockRegistry = {
  'header.standard@1': HeaderBlock,
  'hero.split@1': HeroSplitBlock,
  'text.rich@1': RichTextBlock,
  'content.image-split@1': ImageSplitBlock,
  'features.grid@1': FeatureGridBlock,
  'cta.band@1': CtaBandBlock,
  'form.contact@1': FormBlock,
  'footer.standard@1': FooterBlock
};
